import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { BADGES } from '../hooks/useStreak';

/* Un palier atteint mène à la récompense qu'il a ouverte. */
const BADGE_DESTINATIONS = { 7: '/guides#guide-g4', 21: '/moment' };

export default function BadgeHistory({
  isOpen,
  onClose,
  currentStreak,
  longestStreak,
  activeDaysCount = 0,
  unlockedBadges
}) {
  const [portalEl, setPortalEl] = useState(null);
  const navigate = useNavigate();

  const openBadgeReward = (path) => {
    onClose?.();
    navigate(path, { state: { from: '/' } });
  };

  useEffect(() => {
    const el = document.getElementById('modal-root');
    setPortalEl(el || document.body);
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose]);

  const sortedBadges = useMemo(
    () => [...BADGES].sort((a, b) => a.days - b.days),
    []
  );

const currentBadge = useMemo(() => {
  return [...sortedBadges].reverse().find((badge) => unlockedBadges.some((unlocked) => unlocked.id === badge.id)) || sortedBadges[0];
}, [sortedBadges, unlockedBadges]);

const nextBadge = useMemo(() => {
  return sortedBadges.find((badge) => badge.days > 0 && !unlockedBadges.some((unlocked) => unlocked.id === badge.id)) || null;
}, [sortedBadges, unlockedBadges]);

const progressPercent = useMemo(() => {
  if (!nextBadge) return 100;
  return Math.max(0, Math.min(100, (currentStreak / nextBadge.days) * 100));
}, [currentStreak, nextBadge]);

const remainingDays = nextBadge ? Math.max(0, nextBadge.days - currentStreak) : 0;

  const getThemeVars = (theme = {}) => ({
    '--badge-bg1': theme.bg1 || '#ffffff',
    '--badge-bg2': theme.bg2 || theme.bg1 || '#ffffff',
    '--badge-bg3': theme.bg3 || theme.bg1 || '#ffffff',
    '--badge-border': theme.border || '#e5e7eb',
    '--badge-text': theme.text || '#1f2937',
    '--badge-shadow': theme.shadow || 'rgba(15, 23, 42, 0.08)',
    '--badge-shine': theme.shine || 'rgba(255,255,255,0.45)',
    '--badge-hover-shadow':
      theme.hoverShadow || theme.shadow || 'rgba(15, 23, 42, 0.12)',
  });

  if (!isOpen) return null;
  if (!portalEl) return null;

  const handleBackdropPointerDown = (e) => {
    if (e.target === e.currentTarget) onClose?.();
  };

  return createPortal(
    <div
      className="badge-history-overlay"
      onPointerDown={handleBackdropPointerDown}
      role="dialog"
      aria-modal="true"
      aria-label="Mes badges"
    >
      <div className="badge-history-modal">
        <div className="badge-history-body">
          <div className="badges-premium-card">
            <div className="badges-premium-card__glow" aria-hidden="true" />

            <div className="badges-premium-card__topbar">
              <div>
                <div className="badges-premium-card__kicker">
                  Progression & récompenses
                </div>
                <h2 className="badges-premium-card__title">Mes badges</h2>
              </div>

              <button
                type="button"
                className="badges-premium-card__close"
                onClick={onClose}
                aria-label="Fermer"
              >
                ✕
              </button>
            </div>

            <p className="badges-premium-card__subtitle">
              Chaque jour validé renforce votre série et vous rapproche du prochain niveau.
            </p>

            <div
              className="badges-premium-card__hero"
              style={getThemeVars(currentBadge.theme)}
            >
              <div className="badges-premium-card__hero-shine" aria-hidden="true" />

              <div className="badges-premium-card__hero-top">
                <span className="badges-premium-pill">Niveau atteint</span>
                <span className="badges-premium-pill">
                  {unlockedBadges.length}/{sortedBadges.length} débloqués
                </span>
              </div>

              <div className="badges-premium-card__hero-main">
                <div className="badges-premium-card__hero-emoji">
                  {currentBadge.emoji}
                </div>

                <div className="badges-premium-card__hero-copy">
                  <h3>{currentBadge.name}</h3>
                  <p>{currentBadge.description}</p>
                </div>
              </div>
            </div>

            <div className="badges-premium-stats">
              <div className="badges-premium-stat">
                <div className="badges-premium-stat__icon">🔥</div>
                <strong>{currentStreak}</strong>
                <span>Série actuelle</span>
              </div>

              <div className="badges-premium-stat">
                <div className="badges-premium-stat__icon">⭐</div>
                <strong>{longestStreak}</strong>
                <span>Meilleure série</span>
              </div>

              <div className="badges-premium-stat">
                <div className="badges-premium-stat__icon">🌿</div>
                <strong>{activeDaysCount}</strong>
                <span>Jour{activeDaysCount > 1 ? "s" : ""} au total</span>
              </div>
            </div>

            <div className="badges-premium-progress">
              <div className="badges-premium-progress__head">
                <div>
                  <div className="badges-premium-progress__label">
                    Prochain badge
                  </div>
                  <div className="badges-premium-progress__title">
{nextBadge ? `${nextBadge.emoji} ${nextBadge.name}` : '🏆 Tous les badges débloqués'}
                  </div>
                </div>

                <div className="badges-premium-progress__meta">
                  {nextBadge
                    ? `${remainingDays} jour${remainingDays > 1 ? 's' : ''}`
                    : '100%'}
                </div>
              </div>

              <div className="badges-premium-progress__bar">
                <div
                  className="badges-premium-progress__fill"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>

              <p className="badges-premium-progress__note">
                {nextBadge
                  ? `Encore ${remainingDays} jour${remainingDays > 1 ? 's' : ''} pour débloquer ${nextBadge.name}.`
                  : 'Incroyable : vous avez atteint le plus haut niveau.'}
              </p>
            </div>

            <div className="badges-premium-grid">
              {sortedBadges.map((badge) => {
                const isUnlocked = unlockedBadges.some((b) => b.id === badge.id);
                const isCurrent = badge.id === currentBadge.id;
const progress = badge.days === 0 ? 100 : Math.min(100, (currentStreak / badge.days) * 100);
                const rewardPath = isUnlocked ? BADGE_DESTINATIONS[badge.days] : null;

                return (
                  <div
                    key={badge.id}
                    className={`badge-premium-tile ${isUnlocked ? 'is-unlocked' : 'is-locked'} ${isCurrent ? 'is-current' : ''} ${rewardPath ? 'is-clickable' : ''}`}
                    style={getThemeVars(badge.theme)}
                    role={rewardPath ? 'button' : undefined}
                    tabIndex={rewardPath ? 0 : undefined}
                    onClick={rewardPath ? () => openBadgeReward(rewardPath) : undefined}
                    onKeyDown={rewardPath ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openBadgeReward(rewardPath); } } : undefined}
                  >
                    <div className="badge-premium-tile__shine" aria-hidden="true" />

                    <div className="badge-premium-tile__top">
                      <div className="badge-premium-tile__emoji">{badge.emoji}</div>

                      <div className="badge-premium-tile__chip">
                        {isCurrent
                          ? 'Actuel'
                          : isUnlocked
                          ? 'Débloqué'
                          : `${currentStreak}/${badge.days} jours`}
                      </div>
                    </div>

                    <div className="badge-premium-tile__name">{badge.name}</div>
                    <div className="badge-premium-tile__desc">{badge.description}</div>
                    {badge.reward && <div className="badge-premium-tile__reward">{badge.reward}</div>}
                    {rewardPath && <div className="badge-premium-tile__go">Y accéder →</div>}

                    {!isUnlocked && badge.days > 0 && currentStreak > 0 && (
                      <div className="badge-premium-tile__progress">
                        <div className="badge-premium-tile__progress-bar">
                          <div
                            className="badge-premium-tile__progress-fill"
                            style={{
                              width: `${progress}%`,
                              background: badge.theme?.border || badge.color,
                            }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <style>{`
.badge-history-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: rgba(15, 23, 42, 0.22);
  backdrop-filter: blur(5px);
  -webkit-backdrop-filter: blur(5px);
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100svh;
  --overlay-top: max(12px, env(safe-area-inset-top, 0px) + 12px);
  --overlay-bottom: max(12px, env(safe-area-inset-bottom, 0px) + 12px);
  padding-top: var(--overlay-top);
  padding-bottom: var(--overlay-bottom);
  padding-left: max(8px, env(safe-area-inset-left, 0px));
  padding-right: max(8px, env(safe-area-inset-right, 0px));
  overflow: hidden;
}

.badge-history-modal {
  width: 100%;
  max-width: 860px;
  max-height: calc(100svh - var(--overlay-top) - var(--overlay-bottom));
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: 28px;
  background: transparent;
  box-shadow:
    0 28px 80px rgba(15, 23, 42, 0.16),
    0 0 0 1px rgba(15, 23, 42, 0.04);
}

.badge-history-body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.badge-history-body::-webkit-scrollbar {
  display: none;
}

.badges-premium-card {
  position: relative;
  overflow: hidden;
  border-radius: 28px;
  padding: 24px 14px 20px;
  background:
    radial-gradient(circle at top, rgba(247, 227, 161, 0.24), transparent 32%),
    linear-gradient(160deg, #ffffff 0%, #f8fafc 100%);
  border: 1px solid rgba(217, 170, 88, 0.34);
  box-shadow:
    0 24px 70px rgba(15, 23, 42, 0.14),
    inset 0 1px 0 rgba(255, 255, 255, 0.75);
  color: #1f2937;
}

.badges-premium-card__glow {
  position: absolute;
  inset: -20% auto auto 50%;
  width: 260px;
  height: 260px;
  transform: translateX(-50%);
  background: radial-gradient(
    circle,
    rgba(217, 170, 88, 0.16) 0%,
    rgba(217, 170, 88, 0) 72%
  );
  pointer-events: none;
  filter: blur(14px);
}

.badges-premium-card__topbar {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
}

.badges-premium-card__kicker {
  font-size: 13px;
  font-weight: 700;
  color: #8a6420;
  margin-bottom: 8px;
  letter-spacing: 0.02em;
}

.badges-premium-card__title {
  margin: 0;
  font-family: "Nunito", system-ui, sans-serif;
  font-weight: 900;
  font-size: clamp(28px, 5vw, 34px);
  line-height: 1.05;
  letter-spacing: -0.03em;
  background: linear-gradient(135deg, #2b1d07 0%, #8a6420 46%, #d9aa58 100%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  -webkit-text-fill-color: transparent;
}

.badges-premium-card__close {
  width: 42px;
  height: 42px;
  padding: 0;
  display: grid;
  place-items: center;
  border-radius: 14px;
  border: 1px solid rgba(15, 23, 42, 0.10);
  background: rgba(15, 23, 42, 0.06);
  color: #6b7280;
  font-family: var(--font-sans, "DM Sans", sans-serif);
  font-size: 18px;
  line-height: 1;
  cursor: pointer;
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
}

.badges-premium-card__close:hover {
  background: rgba(15, 23, 42, 0.10);
  color: #1f2937;
}

.badges-premium-card__subtitle {
  position: relative;
  z-index: 1;
  margin: 0 0 18px;
  color: #506866;
  font-family: var(--font-sans, "DM Sans", sans-serif);
  font-size: 0.96rem;
  font-weight: 500;
  line-height: 1.7;
}

.badges-premium-card__hero {
  position: relative;
  z-index: 1;
  overflow: hidden;
  margin: 18px 0 16px;
  padding: 20px 18px;
  border-radius: 24px;
  background: linear-gradient(
    135deg,
    var(--badge-bg3) 0%,
    var(--badge-bg1) 45%,
    var(--badge-bg2) 100%
  );
  border: 1px solid var(--badge-border);
  color: var(--badge-text);
  box-shadow:
    0 18px 36px var(--badge-shadow),
    inset 0 1px 0 rgba(255, 255, 255, 0.35);
}

.badges-premium-card__hero-shine {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    115deg,
    rgba(255, 255, 255, 0) 0%,
    var(--badge-shine) 42%,
    rgba(255, 255, 255, 0) 88%
  );
  transform: translateX(-120%);
  animation: badgesShimmer 5s ease-in-out infinite;
  pointer-events: none;
}

.badges-premium-card__hero-top {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 14px;
}

.badges-premium-pill {
  display: inline-flex;
  align-items: center;
  min-height: 30px;
  padding: 0 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 800;
  background: rgba(255, 255, 255, 0.36);
  border: 1px solid rgba(255, 255, 255, 0.42);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
}

.badges-premium-card__hero-main {
  display: flex;
  align-items: center;
  gap: 14px;
}

.badges-premium-card__hero-emoji {
  width: 74px;
  height: 74px;
  border-radius: 20px;
  display: grid;
  place-items: center;
  font-size: 34px;
  background: rgba(255, 255, 255, 0.34);
  border: 1px solid rgba(255, 255, 255, 0.45);
  box-shadow: 0 10px 22px rgba(0, 0, 0, 0.08);
  flex-shrink: 0;
}

.badges-premium-card__hero-copy h3 {
  margin: 0 0 6px;
  font-family: "Nunito", system-ui, sans-serif;
  font-size: clamp(20px, 4vw, 24px);
  line-height: 1.1;
  color: var(--badge-text);
}

.badges-premium-card__hero-copy p {
  margin: 0;
  font-size: 14px;
  line-height: 1.55;
  color: var(--badge-text);
  opacity: 0.9;
}

.badges-premium-stats {
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
  margin-bottom: 16px;
}

.badges-premium-stat {
  min-height: 120px;
  border-radius: 20px;
  padding: 18px 14px;
  background: rgba(15, 23, 42, 0.04);
  border: 1px solid rgba(15, 23, 42, 0.08);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.75);
  text-align: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.badges-premium-stat__icon {
  font-size: 28px;
  margin-bottom: 10px;
}

.badges-premium-stat strong {
  font-size: clamp(28px, 6vw, 34px);
  line-height: 1;
  font-weight: 900;
  color: #1f2937;
  margin-bottom: 8px;
}

.badges-premium-stat span {
  font-size: 13px;
  font-weight: 700;
  color: #6b7280;
}

.badges-premium-progress {
  position: relative;
  z-index: 1;
  margin-bottom: 18px;
  padding: 18px;
  border-radius: 20px;
  background: rgba(15, 23, 42, 0.04);
  border: 1px solid rgba(15, 23, 42, 0.08);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.75);
}

.badges-premium-progress__head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 12px;
}

.badges-premium-progress__label {
  font-size: 12px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #8a6420;
  margin-bottom: 4px;
}

.badges-premium-progress__title {
  font-size: 16px;
  font-weight: 800;
  color: #1f2937;
}

.badges-premium-progress__meta {
  font-size: 12px;
  font-weight: 800;
  color: #6b7280;
  white-space: nowrap;
}

.badges-premium-progress__bar {
  width: 100%;
  height: 12px;
  border-radius: 999px;
  overflow: hidden;
  background: rgba(15, 23, 42, 0.08);
  box-shadow: inset 0 1px 2px rgba(15, 23, 42, 0.08);
}

.badges-premium-progress__fill {
  height: 100%;
  border-radius: 999px;
  background: linear-gradient(90deg, #f7e3a1 0%, #76a5a7 55%, #4b7cf6 100%);
  box-shadow: 0 0 18px rgba(118, 165, 167, 0.35);
  transition: width 0.35s ease;
}

.badges-premium-progress__note {
  margin: 10px 0 0;
  font-size: 13px;
  line-height: 1.5;
  color: #4b5563;
}

.badges-premium-grid {
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 12px;
}

.badge-premium-tile {
  position: relative;
  overflow: hidden;
  border-radius: 22px;
  padding: 18px 18px 20px;
  background: linear-gradient(
    135deg,
    var(--badge-bg3) 0%,
    var(--badge-bg1) 42%,
    var(--badge-bg2) 100%
  );
  border: 1px solid var(--badge-border);
  color: var(--badge-text);
  box-shadow:
    0 14px 28px var(--badge-shadow),
    inset 0 1px 0 rgba(255, 255, 255, 0.35);
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease,
    opacity 0.2s ease;
}

.badge-premium-tile:hover {
  transform: translateY(-2px);
  box-shadow:
    0 18px 34px var(--badge-hover-shadow),
    inset 0 1px 0 rgba(255, 255, 255, 0.42);
}

.badge-premium-tile.is-current {
  box-shadow:
    0 20px 36px var(--badge-hover-shadow),
    0 0 0 2px rgba(255, 255, 255, 0.45) inset;
}

.badge-premium-tile.is-locked {
  opacity: 0.62;
  filter: grayscale(0.15) saturate(0.85);
}

.badge-premium-tile__shine {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    115deg,
    rgba(255, 255, 255, 0) 0%,
    var(--badge-shine) 42%,
    rgba(255, 255, 255, 0) 88%
  );
  transform: translateX(-120%);
  animation: badgesShimmer 5.2s ease-in-out infinite;
  pointer-events: none;
}

.badge-premium-tile__top {
  position: relative;
  z-index: 1;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
}

.badge-premium-tile__emoji {
  font-size: 30px;
  line-height: 1;
}

.badge-premium-tile__chip {
  min-height: 28px;
  padding: 0 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 800;
  display: inline-flex;
  align-items: center;
  background: rgba(255, 255, 255, 0.34);
  border: 1px solid rgba(255, 255, 255, 0.36);
  color: var(--badge-text);
}

.badge-premium-tile__name {
  position: relative;
  z-index: 1;
  font-family: "Nunito", system-ui, sans-serif;
  font-size: 20px;
  font-weight: 900;
  line-height: 1.12;
  margin-bottom: 6px;
  color: var(--badge-text);
}

.badge-premium-tile__desc {
  position: relative;
  z-index: 1;
  font-size: 14px;
  line-height: 1.5;
  color: var(--badge-text);
  opacity: 0.9;
}

.badge-premium-tile__reward {
  position: relative;
  z-index: 1;
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid rgba(255, 255, 255, 0.28);
  font-size: 13px;
  font-weight: 800;
  line-height: 1.45;
  color: var(--badge-text);
  opacity: 0.92;
}

.badge-premium-tile.is-clickable {
  cursor: pointer;
}

.badge-premium-tile.is-clickable:focus-visible {
  outline: 3px solid rgba(118, 165, 167, 0.4);
  outline-offset: 3px;
}

.badge-premium-tile__go {
  position: relative;
  z-index: 1;
  margin-top: 10px;
  font-size: 12.5px;
  font-weight: 800;
  color: var(--badge-text);
  opacity: 0.78;
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.badge-premium-tile.is-clickable:hover .badge-premium-tile__go {
  opacity: 1;
  transform: translateX(3px);
}

.badge-premium-tile__progress {
  position: relative;
  z-index: 1;
  margin-top: 12px;
}

.badge-premium-tile__progress-bar {
  width: 100%;
  height: 8px;
  border-radius: 999px;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.34);
}

.badge-premium-tile__progress-fill {
  height: 100%;
  border-radius: 999px;
  transition: width 0.35s ease;
}

@keyframes badgesShimmer {
  0% {
    transform: translateX(-120%);
  }
  60% {
    transform: translateX(120%);
  }
  100% {
    transform: translateX(120%);
  }
}

@media (max-width: 640px) {
  .badge-history-overlay {
    padding-left: max(10px, env(safe-area-inset-left, 0px));
    padding-right: max(10px, env(safe-area-inset-right, 0px));
  }

  .badge-history-modal {
    max-width: 100%;
    border-radius: 22px;
  }

  .badges-premium-card {
    border-radius: 22px;
    padding: 20px 8px 16px;
  }

  .badges-premium-card__hero-main {
    align-items: flex-start;
  }

  .badges-premium-card__hero-emoji {
    width: 64px;
    height: 64px;
    font-size: 30px;
    border-radius: 18px;
  }

  .badges-premium-stats {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 8px;
  }

  .badges-premium-stat {
    min-height: 104px;
    padding: 14px 8px;
  }

  .badges-premium-stat strong {
    font-size: clamp(22px, 6vw, 28px);
  }

  .badges-premium-stat span {
    font-size: 11.5px;
    line-height: 1.3;
  }

  .badges-premium-card__title {
    font-size: 28px;
  }

  .badge-premium-tile {
    padding: 16px 16px 18px;
    border-radius: 20px;
  }

  .badge-premium-tile__name {
    font-size: 18px;
  }

  .badges-premium-card__hero {
    padding: 18px 16px;
    border-radius: 22px;
  }

  .badges-premium-progress {
    padding: 16px;
    border-radius: 18px;
  }

  .badges-premium-card__subtitle {
    font-size: 14.5px;
  }
}

        `}</style>
      </div>
    </div>,
    portalEl
  );
}